import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Paper, 
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import { Web, Link as LinkIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useUser } from '../contexts/UserContext';

function CreateSite() {
  const navigate = useNavigate();
  const { refresh } = useUser();
  const [siteName, setSiteName] = useState('');
  const [route, setRoute] = useState('');
  const [siteNameError, setSiteNameError] = useState('');
  const [routeError, setRouteError] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  
  // Configuration settings
  const [requireApproval, setRequireApproval] = useState(true);
  const [isSendWhatsappByDefault, setIsSendWhatsappByDefault] = useState(true);

  // Function to generate URL-friendly route from site name
  const generateRouteFromName = (name: string): string => {
    // Remove special characters and spaces, convert to lowercase
    let route = name
      .toLowerCase()
      .replace(/[\s\-_]+/g, '') // Remove spaces, hyphens, underscores
      .replace(/[^a-z0-9]/g, ''); // Remove non-alphanumeric characters
    
    return route;
  };

  // Debounce function
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Check availability functions
  const checkAvailability = async (field: 'route' | 'siteName', value: string) => {
    if (!value.trim()) return;
    
    setIsCheckingAvailability(true);
    try {
      const payload = { [field]: value };
      const response = await axios.post(`${API_BASE_URL}/auth/check-availability`, payload);
      
      if (!response.data.success && response.data.errors) {
        if (field === 'route') {
          setRouteError(response.data.errors.route);
        } else {
          setSiteNameError(response.data.errors.siteName);
        }
      } else {
        if (field === 'route') {
          setRouteError('');
        } else {
          setSiteNameError('');
        }
      }
    } catch (err) {
      console.error('Error checking availability:', err);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const debouncedCheckRoute = debounce((value: string) => checkAvailability('route', value), 500);
  const debouncedCheckSiteName = debounce((value: string) => checkAvailability('siteName', value), 500);

  // Handle change functions
  const handleRouteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    if (/^[a-z0-9]*$/.test(value)) {
      setRoute(value);
      if (value.trim()) {
        debouncedCheckRoute(value);
      } else {
        setRouteError('');
      }
    }
  };

  const handleSiteNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSiteName(value);
    
    // Auto-suggest route if route is empty or hasn't been manually edited
    if (!route || route === generateRouteFromName(siteName)) {
      const suggestedRoute = generateRouteFromName(value);
      setRoute(suggestedRoute);
      if (suggestedRoute.trim()) {
        debouncedCheckRoute(suggestedRoute);
      }
    }
    
    if (value.trim()) {
      debouncedCheckSiteName(value);
    } else {
      setSiteNameError('');
    }
  };

  // Validation
  const validateFields = () => {
    setError('');
    
    if (!siteName.trim()) {
      setError('請輸入群組名稱');
      return false;
    }
    if (siteNameError) {
      setError(siteNameError);
      return false;
    }
    if (!route.trim()) {
      setError('請輸入群組網址');
      return false;
    }
    if (routeError) {
      setError(routeError);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/sites`,
        {
          name: siteName,
          route: route,
          settings: {
            requireApproval,
            isSendWhatsappByDefault
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data) {
        // Refresh all user data to update roles and sites in the drawer menu
        await refresh();
        // Navigate to share page with site info
        navigate('/share-group', { 
          state: { 
            siteName: siteName,
            route: route 
          } 
        });
      }
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '創建群組失敗，請稍後再試');
      } else {
        setError('創建群組時發生錯誤');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        mt: 8, 
        mb: 8,
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center' 
      }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#000' }}>
            創建新群組
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label="群組名稱"
            autoFocus
            value={siteName}
            onChange={handleSiteNameChange}
            disabled={isLoading}
            error={!!siteNameError}
            helperText={siteNameError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Web color="secondary" />
                </InputAdornment>
              ),
              endAdornment: isCheckingAvailability && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="群組網址"
            value={route}
            onChange={handleRouteChange}
            disabled={isLoading}
            error={!!routeError}
            helperText={routeError || `只能包含小寫英文字母、數字。你的專屬網址將會是https://matchpoint.hk/${route}`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon color="secondary" />
                </InputAdornment>
              ),
              endAdornment: isCheckingAvailability && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
            群組設定
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={requireApproval}
                onChange={(e) => setRequireApproval(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={
              <Box>
                <Typography variant="body1">需要審批</Typography>
                <Typography variant="caption" color="text.secondary">
                  開啟後，新報名需要管理員審批才能確認
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isSendWhatsappByDefault}
                onChange={(e) => setIsSendWhatsappByDefault(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={
              <Box>
                <Typography variant="body1">預設發送 WhatsApp 通知</Typography>
                <Typography variant="caption" color="text.secondary">
                  開啟後，創建活動時會預設勾選發送 WhatsApp 通知
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={isLoading}
            >
              取消
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || isCheckingAvailability}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                '創建群組'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default CreateSite;
